import { formField } from '@epages/react-components'
import { forwardRef, useState } from 'react'
import color from 'color'
import hoistStatics from 'hoist-non-react-statics'

import type { ColorpickerFieldProps } from './ColorpickerField'
import { Popover } from '../Popover'
import type { PopoverAlign, PopoverSide } from '../Popover/types'
import { useTranslate } from '../../utils/translate'
import ColorpickerField from './ColorpickerField'
import FormikValue from './FormikValue'

const colorpickerFieldDimensions = {
  width: 220,
  height: 200,
  hueWidth: 14,
  spacing: 7,
}

type Props = Readonly<{
  value: string
  onChange: (value?: string) => void
  className?: string
  resetValue?: string
  side?: PopoverSide
  align?: PopoverAlign
  offsetDistance?: number
  presetColors?: ColorpickerFieldProps['presetColors']
  onReset?: () => void
}>

export const ColorFieldRaw = forwardRef((props: Props, _ref) => {
  const { value, onChange, className, resetValue, side, align, offsetDistance = 40, presetColors = [], onReset } = props

  const [isOpen, setIsOpen] = useState(false)
  const [intermediateValue, setIntermediateValue] = useState<string | null>(null)

  const t = useTranslate('interface')

  const handleClose = () => {
    setIsOpen(false)
    setIntermediateValue(null)
  }

  const handleReset = () => {
    setIntermediateValue(null)
    onChange(resetValue)

    if (onReset && resetValue === 'transparent') {
      onReset()
    }
  }

  return (
    <Popover
      avoidCollisions
      className="ep-color-settings"
      side={side}
      align={align}
      sideOffset={offsetDistance}
      trigger={<span data-testid="color-field-trigger" className={className} style={{ backgroundColor: value }} />}
      open={isOpen}
      onOpenChange={setIsOpen}
      onEscapeKeyDown={handleClose}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          handleClose()
        }
      }}
    >
      <>
        <ColorpickerField
          className="colorpicker"
          dimensions={colorpickerFieldDimensions}
          value={intermediateValue || value}
          onChangeComplete={() => {
            setIntermediateValue(null)
          }}
          onChange={(value) => {
            setIntermediateValue(value)
            onChange(value)
          }}
          presetColors={presetColors}
        />
        {resetValue && (
          <button
            data-testid="color-field-reset"
            type="button"
            className="ep-color-settings-reset-button"
            onClick={handleReset}
          >
            {t('components.colorFieldComponent.popover.resetButton.label')}
          </button>
        )}
      </>
    </Popover>
  )
})

// https://github.com/yannickcr/eslint-plugin-react/issues/2269
ColorFieldRaw.displayName = 'ColorFieldRaw'

export default hoistStatics(formField()(ColorFieldRaw), ColorFieldRaw)

type FormikColorFieldProps = Readonly<{
  name: string
  className?: string
  withHexInput?: boolean
  [key: string]: any
}>

function FormikColorFieldRaw({ name, withHexInput = false, className = '', ...rest }: FormikColorFieldProps) {
  const [intermediateHexInput, setIntermediateHexInput] = useState<string | null>(null)

  return (
    <FormikValue name={name}>
      {(value, setValue) => (
        <div className={`${className} ${withHexInput ? 'ep-color-field-with-input-wrapper' : ''}`}>
          <ColorFieldRaw value={value} onChange={setValue} className="ep-color-field-box" {...rest} />
          {withHexInput && (
            <input
              type="text"
              className="ep-form-text-field"
              value={intermediateHexInput ?? (value === 'transparent' ? '' : color(value).hex())}
              onBlur={() => {
                setIntermediateHexInput(null)
              }}
              onChange={(e) => {
                setIntermediateHexInput(e.target.value)

                try {
                  setValue(color(e.target.value).hsl().round().string())
                } catch {
                  // invalid color
                }
              }}
            />
          )}
        </div>
      )}
    </FormikValue>
  )
}

export const FormikColorField: typeof FormikColorFieldRaw = hoistStatics(FormikColorFieldRaw, FormikColorFieldRaw)
